import React, { useState, useEffect } from 'react';
import { Radio, Clock, MapPin, Pencil } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { LiveBoardPage } from './LiveBoardPage';
import { TimesheetsPage } from './TimesheetsPage';
import { OutOfAreaRequestsPage } from './OutOfAreaRequestsPage';
import { TimesheetAmendmentRequestsPage } from './TimesheetAmendmentRequestsPage';

type Tab = 'liveboard' | 'timesheets' | 'outofarea' | 'amendments';

const TABS: { id: Tab; label: string; icon: React.ComponentType<{ className?: string }>; description: string }[] = [
  { id: 'liveboard',  label: 'Live Board',            icon: Radio,  description: 'Real-time view of active workers currently signed in on site, including check-in times and assigned locations.' },
  { id: 'timesheets', label: 'Timesheets',             icon: Clock,  description: 'Record and manage worker hours, overtime, and pay rates. View and approve submitted timesheets by date and worker.' },
  { id: 'outofarea',  label: 'Out of Area Requests',  icon: MapPin, description: 'Review and approve requests from workers travelling or operating outside their assigned work area.' },
  { id: 'amendments', label: 'Amendment Requests',   icon: Pencil, description: 'Review and approve worker requests to correct their clock in or clock out times.' },
];

export const OperationsPage: React.FC = () => {
  const [active, setActive] = useState<Tab>('liveboard');
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingAmendmentCount, setPendingAmendmentCount] = useState(0);

  useEffect(() => {
    const checkPending = async () => {
      try {
        const { data, error } = await supabase
          .from('out_of_area_requests')
          .select('id')
          .eq('status', 'pending');
        if (error) return;
        setPendingCount(data?.length || 0);
      } catch {
        // ignore
      }
    };

    const checkPendingAmendments = async () => {
      try {
        const { data, error } = await supabase
          .from('time_amendments')
          .select('id')
          .eq('status', 'pending');
        if (error) return;
        setPendingAmendmentCount(data?.length || 0);
      } catch {
        // ignore
      }
    };

    checkPending();
    checkPendingAmendments();

    const sub = supabase
      .channel('ops_out_of_area')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'out_of_area_requests' }, checkPending)
      .subscribe();

    const amendSub = supabase
      .channel('ops_amendments')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'time_amendments' }, checkPendingAmendments)
      .subscribe();

    return () => {
      supabase.removeChannel(sub);
      supabase.removeChannel(amendSub);
    };
  }, []);

  return (
    <div className="space-y-0">
      <div className="bg-white border-b border-gray-200 -mx-6 px-6 mb-6">
        <nav className="flex gap-1" aria-label="Operations sections">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = active === tab.id;
            const showBadge = (tab.id === 'outofarea' && pendingCount > 0) || (tab.id === 'amendments' && pendingAmendmentCount > 0);
            return (
              <div key={tab.id} className="relative group">
                <button
                  onClick={() => setActive(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  {showBadge && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-xs font-bold text-white bg-orange-500 rounded-full">
                      {tab.id === 'amendments'
                        ? (pendingAmendmentCount > 99 ? '99+' : pendingAmendmentCount)
                        : (pendingCount > 99 ? '99+' : pendingCount)}
                    </span>
                  )}
                </button>
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 hidden group-hover:block">
                  <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl w-56 leading-snug text-center">
                    {tab.description}
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full border-4 border-transparent border-b-gray-900" />
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      <div className={active === 'liveboard'  ? '' : 'hidden'}><LiveBoardPage /></div>
      <div className={active === 'timesheets' ? '' : 'hidden'}><TimesheetsPage /></div>
      <div className={active === 'outofarea'  ? '' : 'hidden'}><OutOfAreaRequestsPage /></div>
      <div className={active === 'amendments' ? '' : 'hidden'}><TimesheetAmendmentRequestsPage /></div>
    </div>
  );
};
